import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardDescription } from "../ui/card";
import api from "../../lib/api";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
} from "../ui/table";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useToast } from "../../hooks/use-toast";
import { Copy } from "lucide-react";
import { Input } from "../ui/input";
const Queues = () => {
  const [allQueueCounts, setAllQueueCounts] = useState<any>({});
  const [selectedQueueName, setSelectedQueueName] = useState<string>("orderQueue");
  const [queueCounts, setQueueCounts] = useState({
    waiting: 0,
    active: 0,
    completed: 0,
    failed: 0,
  });
  const [currentQueue, setCurrentQueue] = useState<string | null>(null);
  const [queueJobs, setQueueJobs] = useState<any[]>([]);
  const [stacktrace, setStacktrace] = useState<string | null>(null);
  const [jobBody, setJobBody] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    const fetchQueueCounts = async () => {
      const counts = await api.getQueueCounts();
      setAllQueueCounts(counts);
      // Set default to orderQueue
      if (counts.orderQueue) {
        setQueueCounts({
          waiting: counts.orderQueue.waiting || 0,
          active: counts.orderQueue.active || 0,
          completed: counts.orderQueue.completed || 0,
          failed: counts.orderQueue.failed || 0,
        });
      }
    };
    fetchQueueCounts();
  }, []);

  const getQueueJobs = async (
    status: string,
    pageNum = page,
    limitNum = limit,
    searchTerm = search,
  ) => {
    let searchParam = searchTerm;
    if (searchTerm.length <= 2) {
      searchParam = "";
    }
    const response = await api.getQueueJobs(
      status,
      pageNum,
      limitNum,
      searchParam,
      selectedQueueName,
    );
    setQueueJobs(response.jobs);
    setTotal(response.total);
  };

  const switchQueue = (queueName: string) => {
    setSelectedQueueName(queueName);
    if (allQueueCounts[queueName]) {
      setQueueCounts({
        waiting: allQueueCounts[queueName].waiting || 0,
        active: allQueueCounts[queueName].active || 0,
        completed: allQueueCounts[queueName].completed || 0,
        failed: allQueueCounts[queueName].failed || 0,
      });
    }
    setCurrentQueue(null);
    setQueueJobs([]);
  };

  const showStacktrace = (jobId: string) => {
    const job = queueJobs.find(job => job.id === jobId);
    if (job) {
      setStacktrace(`${job.failedReason}\n\n${job.stacktrace}`);
      setJobBody(job.data);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const retryJob = async (jobId: string) => {
    const response = await api.retryQueueJob(jobId);
    if (response.success) {
      getQueueJobs(currentQueue!, page);
      toast({
        title: "Success",
        description: "Job retried successfully",
      });
    } else {
      toast({
        title: "Error",
        description: response.error,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    getQueueJobs(currentQueue!, page, limit, search);
  }, [currentQueue, page, limit, search]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1>Queue Monitoring</h1>
        <Select value={selectedQueueName} onValueChange={switchQueue}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select Queue" />
          </SelectTrigger>
          <SelectContent>
            {Object.keys(allQueueCounts).map((queueName) => (
              <SelectItem key={queueName} value={queueName}>
                {queueName === 'orderQueue' ? 'Order Queue' : 'Batch Queue'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <h2 className="text-lg font-semibold mb-2">
        {selectedQueueName === 'orderQueue' ? 'Order Queue' : 'Batch Queue'} Status
      </h2>
      <div className="grid mt-2 grid-cols-4 gap-4">
        {Object.entries(queueCounts).map(([key, value]) => (
          <Card
            key={key}
            onClick={() => {
              setCurrentQueue(key);
              setSearch("");
            }}
            className={`cursor-pointer ${
              currentQueue === key ? "border-blue-500" : ""
            }`}
          >
            <CardHeader>
              <CardTitle>{key}</CardTitle>
              <CardDescription>{value}</CardDescription>
            </CardHeader>
          </Card>
        ))}
      </div>
      {currentQueue && (
        <div className="mt-4">
          <Input
            placeholder="Search by job name or data"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="mb-2"
          />
          <h2 className="text-lg font-bold">{currentQueue} Jobs</h2>
          <div className="mt-2">
            <Table className="w-full bg-white">
              <TableHeader>
                <TableRow>
                  <TableHead>Job ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Processed At</TableHead>
                  <TableHead>Finished At</TableHead>
                  <TableHead>Order ID</TableHead>
                  {currentQueue === "failed" && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {queueJobs?.map(job => (
                  <TableRow key={job.id}>
                    <TableCell>{job.id}</TableCell>
                    <TableCell>{job.name}</TableCell>
                    <TableCell>
                      {new Date(job.processedOn).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {new Date(job.finishedOn).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {job.data.order_id || job.data.orderData?.order_id}
                    </TableCell>
                    {currentQueue === "failed" && (
                      <TableCell>
                        <Button
                          variant="outline"
                          onClick={() => showStacktrace(job.id)}
                        >
                          Stacktrace
                        </Button>
                        <Button
                          variant="outline"
                          className="ml-2"
                          onClick={() => retryJob(job.id)}
                        >
                          Retry
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>Rows per page:</span>
              <Select
                value={limit.toString()}
                onValueChange={value => {
                  setLimit(Number(value));
                  setPage(1);
                  getQueueJobs(currentQueue!, 1, Number(value));
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue placeholder={limit} />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50].map(value => (
                    <SelectItem key={value} value={value.toString()}>
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span>
                {(page - 1) * limit + 1}-{Math.min(page * limit, total)} of{" "}
                {total}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newPage = page - 1;
                    setPage(newPage);
                    getQueueJobs(currentQueue!, newPage);
                  }}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newPage = page + 1;
                    setPage(newPage);
                    getQueueJobs(currentQueue!, newPage);
                  }}
                  disabled={page >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {(stacktrace || jobBody) && (
        <div className="modal fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-4 rounded-lg w-1/2 overflow-auto max-h-[90vh]">
            <div className="flex justify-between">
              <h2 className="text-lg font-bold">Stacktrace</h2>
              <Button
                variant="outline"
                onClick={() => {
                  setStacktrace(null);
                  setJobBody(null);
                }}
              >
                X
              </Button>
            </div>
            {stacktrace && (
              <pre className="text-sm whitespace-pre-wrap break-words bg-gray-100 p-4 rounded-lg">
                {stacktrace}
              </pre>
            )}
            {jobBody && (
              <div className="mt-4">
                <div className="flex justify-between">
                  <h2 className="text-lg font-bold">Data body</h2>
                  <Copy
                    onClick={() => {
                      navigator.clipboard.writeText(
                        JSON.stringify(jobBody, null, 2),
                      );
                      toast({
                        title: "Copied to clipboard",
                      });
                    }}
                    className="cursor-pointer"
                  />
                </div>
                <pre className="text-sm whitespace-pre-wrap break-words bg-gray-100 p-4 rounded-lg">
                  {JSON.stringify(jobBody, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Queues;
